import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })
      .format(date).replace(":", ".").replace(/\s+/g, " ").trim();
    return `${datePart} ${timePart}`;
  } catch { return String(value); }
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  try { return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)); }
  catch { return String(value); }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "-";
  if (startDate && endDate) return `${formatDateOnly(startDate)} to ${formatDateOnly(endDate)}`;
  return formatDateOnly(startDate) || formatDateOnly(endDate) || "-";
};

const toText = (value, fallback = "-") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

export function TaskListPdfDocument({
  components,
  logoUrl,
  companyInfo,
  filters,
  report,
  selectedColumns,
  generatedAt,
}) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;
  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : ["task", "ticket", "assignee", "priority", "status", "activity", "actions"];
  const serialColumnWidth = 22;
  const columnWidth = { flex: 1 };

  const styles = StyleSheet.create({
    page: {
      paddingTop: 28,
      paddingBottom: 28,
      paddingHorizontal: 30,
      backgroundColor: "#ffffff",
      fontSize: 8.6,
      color: "#0f172a",
      fontFamily: "HindSiliguri",
    },

    /* ── Header ── */
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 10,
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      marginBottom: 12,
    },
    brandRow: { flexDirection: "column", alignItems: "flex-start" },
    logo: { width: 100, objectFit: "contain", marginBottom: 2 },
    companyName: { fontSize: 15, fontWeight: 700, color: "#0f172a", letterSpacing: -0.2 },
    companyTagline: { fontSize: 8, color: "#64748b", marginTop: 1, letterSpacing: 0.3 },
    contactBlock: { alignItems: "flex-start" },
    contactRow: { flexDirection: "row", alignItems: "center", marginBottom: 3 },
    contactLabel: { fontSize: 8, fontWeight: 700, color: "#0f172a", width: 52 },
    contactLine: { fontSize: 8, color: "#475569" },

    /* ── Meta bar ── */
    metaBar: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 6,
      paddingVertical: 7,
      paddingHorizontal: 11,
      marginBottom: 14,
    },
    metaLeft: { flexDirection: "column" },
    metaReportName: { fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 1 },
    metaFilter: { fontSize: 7.8, color: "#475569", lineHeight: 1.55 },
    metaRight: { alignItems: "flex-end" },
    metaGenLabel: { fontSize: 7, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 1 },
    metaGenValue: { fontSize: 8, color: "#334155" },

    /* ── Table ── */
    table: {
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 7,
      overflow: "hidden",
    },
    tHead: {
      flexDirection: "row",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tHeadCell: {
      fontSize: 7.2,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 6,
    },
    tRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#f1f5f9",
      paddingVertical: 6,
      paddingHorizontal: 4,
    },
    tRowAlt: { backgroundColor: "#f8fafc" },
    tRowLast: { borderBottomWidth: 0 },
    cell: { paddingHorizontal: 6 },
    cellStrong: { fontSize: 7.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 },
    cellText: { fontSize: 7.1, color: "#475569", lineHeight: 1.35, marginTop: 1 },
    serialHeadCell: {
      fontSize: 7.2,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 6,
      textAlign: "center",
    },
    serialCell: {
      paddingHorizontal: 6,
      fontSize: 7.5,
      fontWeight: 700,
      color: "#0f172a",
      textAlign: "center",
    },

    /* ── Empty / Footer ── */
    emptyBox: {
      borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 7,
      paddingVertical: 20, alignItems: "center",
    },
    emptyText: { fontSize: 9, color: "#94a3b8" },
    footer: {
      marginTop: 10, paddingTop: 8,
      borderTopWidth: 1, borderTopColor: "#e2e8f0",
      flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    },
    footerText: { fontSize: 7.6, color: "#94a3b8" },
  });

  const statusCounts = Array.isArray(report?.statusCounts) ? report.statusCounts : [];
  const countsByStatus = statusCounts.reduce((result, item) => {
    result[item?.status] = Number(item?.count || 0);
    return result;
  }, {});
  const totalItems = report?.items?.length || 0;

  const filterParts = [
    `Date: ${formatDateRange(filters?.startDate, filters?.endDate)}`,
    filters?.requestedUserName ? `User: ${filters.requestedUserName}` : null,
    filters?.status && filters.status !== "All Statuses" ? `Status: ${filters.status}` : null,
  ].filter(Boolean);

  const columns = [
    {
      key: "task",
      header: "Task",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.title, "Untitled task")}</Text>
          <Text style={styles.cellText}>{toText(item.description, "No description")}</Text>
          <Text style={styles.cellText}>Created by {toText(item.createdByName, "Unknown")} | Due {formatDateOnly(item.dueDate)}</Text>
        </View>
      ),
    },
    {
      key: "ticket",
      header: "Ticket",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.ticketNumber, "Manual task")}</Text>
          <Text style={styles.cellText}>{toText(item.ticketSubject, "No linked ticket subject")}</Text>
        </View>
      ),
    },
    {
      key: "assignee",
      header: "Assignee",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.assignedEmployeeName, "Unassigned")}</Text>
          <Text style={styles.cellText}>{toText(item.assignedEmployeeEmail || item.assignedUserId, "No email")}</Text>
        </View>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.priority, "Low")}</Text>
        </View>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{toText(item.status, "Pending")}</Text>
          <Text style={styles.cellText}>Progress {Number(item.progressPercent || 0)}%</Text>
        </View>
      ),
    },
    {
      key: "activity",
      header: "Last Activity",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{formatDateTime(item.lastActivityAt || item.updatedAt)}</Text>
          <Text style={styles.cellText}>{toText(item.lastActionLabel, "Task Updated")} by {toText(item.lastActorName, "Unknown")}</Text>
          <Text style={styles.cellText}>{toText(item.lastNote, "No note")}</Text>
        </View>
      ),
    },
    {
      key: "actions",
      header: "Source",
      render: (item) => (
        <View style={[styles.cell, columnWidth]}>
          <Text style={styles.cellStrong}>{item.ticketId ? "Ticket / Task" : "Task Desk"}</Text>
          <Text style={styles.cellText}>{item.ticketId ? `Ticket ${toText(item.ticketNumber, "Open")}` : "Manual task"}</Text>
        </View>
      ),
    },
  ].filter((column) => activeColumns.includes(column.key));

  return (
    <Document
      title="Task Report"
      author="BanglaFire CRM"
      subject="Task report"
      creator="BanglaFire CRM"
      producer="BanglaFire CRM"
    >
      <Page size="A4" orientation="landscape" style={styles.page}>

        {/* ── HEADER ── */}
        <View style={styles.header}>
          <View style={styles.brandRow}>
            {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
            <Text style={styles.companyName}>{toText(companyInfo?.name, "BanglaFire CRM")}</Text>
            <Text style={styles.companyTagline}>Customer Relationship Management</Text>
          </View>
          <View style={styles.contactBlock}>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Address :</Text>
              <Text style={styles.contactLine}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Mobile :</Text>
              <Text style={styles.contactLine}>(+880) 9639222999  |  1709927934</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Email :</Text>
              <Text style={styles.contactLine}>md@banglafire.com  |  support@banglafire.com</Text>
            </View>
          </View>
        </View>

        {/* ── META BAR ── */}
        <View style={styles.metaBar}>
          <View style={styles.metaLeft}>
            <Text style={styles.metaReportName}>Task Report</Text>
            {filterParts.length > 0 && (
              <Text style={styles.metaFilter}>{filterParts.join("   •   ")}</Text>
            )}
          </View>
          <View style={styles.metaRight}>
            <Text style={styles.metaGenLabel}>Generated</Text>
            <Text style={styles.metaGenValue}>{formatDateTime(generatedAt)}</Text>
            <Text style={[styles.metaGenLabel, { marginTop: 4 }]}>Total Records</Text>
            <Text style={[styles.metaGenValue, { fontWeight: 700, color: "#0f172a" }]}>{totalItems}</Text>
            <Text style={[styles.metaGenLabel, { marginTop: 4 }]}>Completed / Pending</Text>
            <Text style={styles.metaGenValue}>{countsByStatus.Completed || 0} / {countsByStatus.Pending || 0}</Text>
          </View>
        </View>

        {/* ── TABLE ── */}
        {totalItems > 0 ? (
          <View style={styles.table}>
            <View style={styles.tHead}>
              <Text style={[styles.serialHeadCell, { width: serialColumnWidth }]}>#</Text>
              {columns.map((column) => (
                <Text key={column.key} style={[styles.tHeadCell, columnWidth]}>
                  {column.header}
                </Text>
              ))}
            </View>
            {report.items.map((item, index) => (
              <View
                key={item.taskId || `${item.title}-${index}`}
                style={[styles.tRow, index % 2 === 1 ? styles.tRowAlt : null, index === report.items.length - 1 ? styles.tRowLast : null]}
                wrap={false}
              >
                <Text style={[styles.serialCell, { width: serialColumnWidth }]}>{index + 1}</Text>
                {columns.map((column) => (
                  <React.Fragment key={column.key}>
                    {column.render(item)}
                  </React.Fragment>
                ))}
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No tasks matched the selected report filters.</Text>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM  |  Confidential</Text>
        </View>
      </Page>
    </Document>
  );
}
